import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Flame, MapPinned, AlarmClock } from 'lucide-react';

const DAY_NAMES = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
const HOURS = Array.from({ length: 18 }, (_, i) => i + 6); // 06–23

/** Day-of-week × hour heatmap. Colour intensity scales with the local max so
 * the visual stays useful even with small datasets. */
const Heatmap = ({ rows }) => {
  // Build a sparse {day_num: {hour: count}} matrix
  const matrix = Array.from({ length: 6 }, () => Array(HOURS.length).fill(0));
  let max = 0;
  rows.forEach((r) => {
    if (r.day_num >= 0 && r.day_num < 6) {
      const idx = r.hour - 6;
      if (idx >= 0 && idx < HOURS.length) {
        matrix[r.day_num][idx] = r.count;
        if (r.count > max) max = r.count;
      }
    }
  });
  if (max === 0) return <p className="text-xs text-muted-foreground py-8 text-center">No data for the current filter.</p>;

  const cellColor = (v) => {
    if (!v) return 'rgba(45, 140, 140, 0.04)';
    const ratio = Math.min(v / max, 1);
    return `rgba(45, 140, 140, ${0.18 + ratio * 0.82})`;
  };

  return (
    <div className="overflow-x-auto" data-testid="picking-heatmap">
      <table className="text-[11px] border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th className="w-8" />
            {HOURS.map((h) => (
              <th key={h} className="font-medium text-muted-foreground text-center w-7">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DAY_NAMES.map((d, i) => (
            <tr key={d}>
              <td className="font-semibold text-muted-foreground text-right pr-1">{d}</td>
              {matrix[i].map((v, j) => (
                <td
                  key={j}
                  title={`${d} ${HOURS[j]}:00 — ${v} pick${v !== 1 ? 's' : ''}`}
                  className="text-center font-medium align-middle"
                  style={{
                    background: cellColor(v),
                    color: v && v / max > 0.55 ? '#fff' : '#1d2939',
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                  }}
                >
                  {v || ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DeepInsights = ({ analytics }) => {
  if (!analytics) return null;
  const heatmap = analytics.heatmap || [];
  const slowLoc = analytics.top_slow_locations || [];
  const slowTasks = analytics.slowest_tasks || [];

  if (!heatmap.length && !slowLoc.length && !slowTasks.length) return null;

  return (
    <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3" data-testid="picking-deep-insights">
      <Card className="xl:col-span-2">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Flame className="h-4 w-4 text-orange-500" /> Peak-hour heatmap
          </CardTitle>
          <CardDescription className="text-xs">Picks per day-of-week × hour. Darker = busier.</CardDescription>
        </CardHeader>
        <CardContent><Heatmap rows={heatmap} /></CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPinned className="h-4 w-4 text-rose-500" /> Slowest start locations
          </CardTitle>
          <CardDescription className="text-xs">Highest avg pick time · ≥3 samples.</CardDescription>
        </CardHeader>
        <CardContent>
          {slowLoc.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">Not enough data.</p>
          ) : (
            <div className="space-y-1 text-xs">
              <div className="grid grid-cols-[1fr_auto_auto] gap-2 font-semibold text-muted-foreground border-b pb-1">
                <span>Location</span>
                <span className="text-right">Tasks</span>
                <span className="text-right">Avg min</span>
              </div>
              {slowLoc.map((l) => (
                <div key={l.location} className="grid grid-cols-[1fr_auto_auto] gap-2 py-0.5 border-b last:border-0" data-testid={`slow-loc-${l.location}`}>
                  <span className="font-mono truncate">{l.location}</span>
                  <span className="text-right text-muted-foreground">{l.tasks}</span>
                  <span className="text-right font-bold">{l.avg_pick_time.toFixed(1)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="xl:col-span-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlarmClock className="h-4 w-4 text-amber-500" /> Slowest individual tasks
          </CardTitle>
          <CardDescription className="text-xs">Top 10 outliers. Useful for spotting one-off bottlenecks (mis-picks, missing stock, equipment stalls).</CardDescription>
        </CardHeader>
        <CardContent>
          {slowTasks.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No data.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="slowest-tasks-table">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-1 pr-2">Order</th>
                    <th className="text-left py-1 pr-2">Wachtrij</th>
                    <th className="text-left py-1 pr-2">Bewerker</th>
                    <th className="text-left py-1 pr-2">Start loc.</th>
                    <th className="text-left py-1 pr-2">When</th>
                    <th className="text-right py-1">Pick time (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {slowTasks.map((t, i) => (
                    <tr key={t.task_id || i} className="border-b last:border-0">
                      <td className="font-mono py-1 pr-2">{t.voorraadorder}</td>
                      <td className="py-1 pr-2">{t.wachtrij}</td>
                      <td className="py-1 pr-2">{t.bewerker}</td>
                      <td className="font-mono py-1 pr-2">{t.startloc}</td>
                      <td className="py-1 pr-2">{t.start_datetime ? new Date(t.start_datetime).toLocaleString() : ''}</td>
                      <td className="text-right font-bold py-1">{t.pick_time_min.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DeepInsights;
